import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import { LayoutContentFallback } from './AppBootScreen';
import {
  ArrowLeft,
  Loader2,
  LogIn,
} from 'lucide-react';
import { SuperAdminLayout } from './SuperAdminLayout';
import { AdminEmbedProvider } from '../contexts/AdminEmbedContext';
import { CompanyAdminAuthContext } from '../contexts/CompanyAdminAuthContext';
import api from '../lib/api';
import {
  setActAsCompanyToken,
  clearActAsCompanyToken,
} from '../lib/companyAdminApi';
import { SUPERADMIN_COMPANY_NAV_SECTIONS } from '../lib/superadminCompanyNav';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE_URL } from '../lib/utils';
import { loginAsDietitianAdmin } from '../lib/superadminActAs';

export default function CompanyDetailLayout() {
  const { company_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [openingFullAdmin, setOpeningFullAdmin] = useState(false);

  const openFullAdmin = async () => {
    setOpeningFullAdmin(true);
    try {
      await loginAsDietitianAdmin(company_id, { newTab: true });
      toast.success('Opened full dietitian admin panel');
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || 'Failed to open admin panel',
      );
    } finally {
      setOpeningFullAdmin(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      setLoading(true);
      setError(null);
      clearActAsCompanyToken();
      try {
        const res = await api.post(`/superadmin/companies/${company_id}/act-as`);
        if (cancelled) return;
        const accessToken = res.data?.accessToken;
        if (!accessToken) throw new Error('No act-as token');
        setActAsCompanyToken(accessToken);
        setToken(accessToken);
        setCompany(res.data.company || null);
        setUser(res.data.user || null);

        try {
          const cfgRes = await axios.get(
            `${API_BASE_URL}/admin/company/config`,
            { headers: { Authorization: `Bearer ${accessToken}` } },
          );
          if (!cancelled) setCompanyConfig(cfgRes.data || { ai_chat: 'enabled' });
        } catch {
          if (!cancelled) setCompanyConfig({ ai_chat: 'enabled' });
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || 'Failed to open company admin');
          toast.error(e.response?.data?.message || 'Failed to open company admin');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    boot();
    return () => {
      cancelled = true;
      clearActAsCompanyToken();
    };
  }, [company_id]);

  const authValue = useMemo(
    () => ({
      company,
      user,
      token,
      login: () => {},
      logout: () => {},
      isAuthenticated: !!token,
      loading: false,
      userPermissions: null,
      hasPermission: () => true,
      canAction: () => true,
      companyConfig,
      refreshPermissions: () => Promise.resolve(),
      refreshUser: () => Promise.resolve(),
    }),
    [company, user, token, companyConfig],
  );

  const base = `/superadmin/companies/${company_id}`;

  const isActive = useCallback(
    (item) => {
      const full = item.path ? `${base}/${item.path}` : base;
      const pathMatch =
        location.pathname === full || location.pathname === `${full}/`;
      if (item.tab) {
        if (!pathMatch) return false;
        const currentTab = new URLSearchParams(location.search).get('tab');
        if (!currentTab) return item.tab === 'ai_breakdown';
        return currentTab === item.tab;
      }
      if (item.end || !item.path) {
        return pathMatch;
      }
      return location.pathname === full || location.pathname.startsWith(`${full}/`);
    },
    [base, location.pathname, location.search],
  );

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex flex-1 items-center justify-center min-h-0 h-full">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </SuperAdminLayout>
    );
  }

  if (error || !token) {
    return (
      <SuperAdminLayout>
        <div className="p-8 overflow-auto h-full">
          <button
            type="button"
            onClick={() => navigate('/superadmin/companies')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to companies
          </button>
          <p className="text-destructive">{error || 'Unable to load company'}</p>
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <AdminEmbedProvider companyId={company_id} company={company}>
        <CompanyAdminAuthContext.Provider value={authValue}>
          <div
            className="flex flex-1 min-h-0 h-full w-full"
            data-testid="company-detail-layout"
          >
            {/* Company sidebar — independent scroll */}
            <aside className="hidden sm:flex w-64 shrink-0 border-r border-border bg-card flex-col min-h-0 h-full">
              <div className="shrink-0 p-4 border-b border-border space-y-3 bg-card">
                <button
                  type="button"
                  onClick={() => navigate('/superadmin/companies')}
                  className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Companies
                </button>
                <div className="min-w-0">
                  <p className="text-[11px] text-muted-foreground uppercase tracking-wide">
                    Company Admin
                  </p>
                  <h2 className="font-semibold text-foreground truncate">
                    {company?.name || `Company #${company_id}`}
                  </h2>
                  <p className="text-xs text-muted-foreground truncate">
                    {company?.email}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openFullAdmin}
                  disabled={openingFullAdmin}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
                >
                  {openingFullAdmin ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <LogIn className="w-3.5 h-3.5" />
                  )}
                  Login as dietitian
                </button>
              </div>
              <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-4 scrollbar-thin">
                {SUPERADMIN_COMPANY_NAV_SECTIONS.map((section) => (
                  <div key={section.title}>
                    <p className="px-2 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {section.title}
                    </p>
                    <div className="space-y-0.5">
                      {section.items.map((item) => {
                        const to = item.path
                          ? `${base}/${item.path}${item.search || ''}`
                          : base;
                        const active = isActive(item);
                        const Icon = item.icon;
                        return (
                          <Link
                            key={`${item.path || 'overview'}${item.search || ''}`}
                            to={to}
                            className={cn(
                              'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors',
                              active
                                ? 'bg-muted text-foreground font-medium'
                                : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground',
                            )}
                          >
                            <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                            <span className="truncate">{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </nav>
            </aside>

            {/* Page content — independent scroll */}
            <main className="flex-1 min-w-0 min-h-0 h-full overflow-y-auto overscroll-contain">
              {/* Mobile company nav */}
              <div className="sm:hidden sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur px-3 py-2">
                <button
                  type="button"
                  onClick={() => navigate('/superadmin/companies')}
                  className="flex items-center gap-2 text-xs text-muted-foreground mb-2"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Companies
                </button>
                <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                  {SUPERADMIN_COMPANY_NAV_SECTIONS.flatMap((s) => s.items).map((item) => {
                    const to = item.path
                      ? `${base}/${item.path}${item.search || ''}`
                      : base;
                    const active = isActive(item);
                    return (
                      <Link
                        key={`${item.path || 'overview'}${item.search || ''}`}
                        to={to}
                        className={cn(
                          'shrink-0 px-2.5 py-1.5 rounded-md text-xs whitespace-nowrap border transition-colors',
                          active
                            ? 'bg-muted border-border text-foreground font-medium'
                            : 'border-transparent text-muted-foreground hover:bg-muted/60',
                        )}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <Suspense fallback={<LayoutContentFallback />}>
                  <Outlet />
                </Suspense>
              </div>
            </main>
          </div>
        </CompanyAdminAuthContext.Provider>
      </AdminEmbedProvider>
    </SuperAdminLayout>
  );
}
